import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { LANGS, applyLang } from "../lib/i18n";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, ChevronLeft } from "lucide-react";

export function Login() {
  const [params] = useSearchParams();
  // Driver/Passenger tab lets each audience see the login screen framed for them
  // and drops them to the right register CTA. Preselect from ?role= for deep links.
  const initialRole = params.get("role") === "supplier" ? "supplier" : "customer";
  const [role, setRole] = useState(initialRole);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();

  // When the PayPal return page bounces here after a session mismatch, it
  // appends ?hint=<masked-email>. Show it above the form so the user knows
  // which account started the payment.
  const hint = params.get("hint");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      // If the user picked a tab that doesn't match their actual role, don't
      // block them — just warn once and route them to their real dashboard.
      if (role !== "admin" && u.role !== "admin" && u.role !== role) {
        toast.info(
          u.role === "supplier"
            ? t("auth.roleMismatchDriver", "This is a driver account — signing you in as a driver.")
            : t("auth.roleMismatchPassenger", "This is a passenger account — signing you in as a passenger.")
        );
      } else {
        toast.success(t("auth.welcome"));
      }
      // Honor ?next=/paypal/return?... so an interrupted PayPal capture flow
      // (or any other post-login resume) picks up where it left off.
      const nextRaw = new URLSearchParams(window.location.search).get("next");
      if (nextRaw && nextRaw.startsWith("/")) {
        nav(nextRaw, { replace: true });
        return;
      }
      nav(u.role === "admin" ? "/admin" : u.role === "supplier" ? "/supplier" : "/customer");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const isDriver = role === "supplier";

  return <AuthShell title={t("auth.signIn")} subtitle={isDriver ? t("auth.signInSubDriver", "Drive with us — start earning.") : t("auth.signInSub")}>
    <div className="mb-4 grid grid-cols-2 gap-2 p-1 bg-ink-surface2 border border-ink-border rounded-full" data-testid="login-role-tabs">
      <button
        type="button"
        onClick={() => setRole("customer")}
        className={`rounded-full py-2 text-[11px] font-black uppercase tracking-widest transition-all ${!isDriver ? "bg-white text-black" : "text-ink-textDim"}`}
        data-testid="login-role-passenger"
      >
        {t("auth.rolePassenger", "Passenger")}
      </button>
      <button
        type="button"
        onClick={() => setRole("supplier")}
        className={`rounded-full py-2 text-[11px] font-black uppercase tracking-widest transition-all ${isDriver ? "bg-brand-accent text-black" : "text-ink-textDim"}`}
        data-testid="login-role-driver"
      >
        {t("auth.roleDriver", "Driver")}
      </button>
    </div>
    <form onSubmit={submit} className="space-y-3" data-testid="login-form">
      {hint ? (
        <div
          data-testid="login-paypal-hint"
          className="rounded-2xl border border-brand-warn/40 bg-brand-warn/10 px-3 py-2 text-[12px] text-brand-warn"
        >
          {t("auth.paypalResumeHint",
             "Continue your PayPal payment — sign in with")} <b>{hint}</b>
        </div>
      ) : null}
      <Field label={t("auth.email")} value={email} onChange={setEmail} type="email" testid="login-email" />
      <Field label={t("auth.password")} value={password} onChange={setPassword} type="password" testid="login-password" />
      <button type="submit" disabled={busy} className={`pill-btn w-full rounded-full py-3.5 font-black flex items-center justify-center gap-2 disabled:opacity-50 ${isDriver ? "bg-brand-accent text-black" : "bg-white text-black"}`} data-testid="login-submit">
        {busy && <Loader2 className="animate-spin" size={16} />}
        {isDriver ? t("auth.signInDriver", "Sign in as Driver") : t("auth.signInPassenger", "Sign in as Passenger")}
      </button>
      <div className="text-center pt-1">
        <Link to="/forgot-password" className="text-[11px] text-ink-textDim hover:text-white underline underline-offset-4" data-testid="link-forgot-password">
          {t("auth.forgotPassword", "Forgot password?")}
        </Link>
      </div>
    </form>
    <GoogleAuthButton />
    <div className="text-center text-xs text-ink-textDim mt-6">
      {isDriver
        ? <>{t("auth.noDriverAccount", "New driver?")} <Link to="/register?role=supplier" className="text-brand-accent font-black" data-testid="link-register-driver">{t("auth.signUpDriver", "Sign up to drive")}</Link></>
        : <>{t("auth.noAccount")} <Link to="/register" className="text-white font-black" data-testid="link-register">{t("auth.signUp")}</Link></>}
    </div>
  </AuthShell>;
}

export function Register() {
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "supplier" ? "supplier" : "customer");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [picture, setPicture] = useState(null);
  const [plate, setPlate] = useState("");
  const [color, setColor] = useState("");
  const [model, setModel] = useState("");
  const [supplierCategory, setSupplierCategory] = useState("");
  const [taxiType, setTaxiType] = useState("");
  const [busType, setBusType] = useState("");
  const [seatingCapacity, setSeatingCapacity] = useState("");
  const [truckBand, setTruckBand] = useState("");
  const [craneBand, setCraneBand] = useState("");
  const [luxuryStyle, setLuxuryStyle] = useState("");
  const [luxuryHourlyRate, setLuxuryHourlyRate] = useState("");
  const [luxuryDailyRate, setLuxuryDailyRate] = useState("");
  const [specs, setSpecs] = useState(null);
  const [busy, setBusy] = useState(false);
  // Pre-fill invite code from `?ref=DR-XYZ` deep links (share URLs).
  const [inviteCode, setInviteCode] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return (p.get("ref") || "").toUpperCase();
    } catch { return ""; }
  });
  const { register } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();

  // Load server-authoritative taxi/truck spec options
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vehicle-specs`)
      .then((r) => r.json())
      .then((data) => setSpecs(data))
      .catch(() => setSpecs({}));
  }, []);
  const truckCats = specs?.truck?.categories || ["moving", "towing"];
  const isTruckCat = truckCats.includes(supplierCategory);
  const isTaxiCat = supplierCategory === "taxi";
  const isBusCat = supplierCategory === "bus";
  const isCraneCat = supplierCategory === "crane";
  const isLuxuryCat = supplierCategory === "luxury";

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error(t("auth.photoTooLarge", "Photo too large (max 500KB)"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPicture(String(reader.result));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name, email, password, role };
      if (inviteCode.trim()) payload.referred_by_code = inviteCode.trim().toUpperCase();
      if (role === "supplier") {
        payload.phone = phone;
        if (picture) payload.picture = picture;
        if (supplierCategory) payload.primary_category = supplierCategory;
        const veh = { plate, color, model };
        if (isTaxiCat && taxiType) veh.taxi_type = taxiType;
        if (isBusCat && busType) veh.bus_type = busType;
        if (seatingCapacity) veh.seating_capacity = parseInt(seatingCapacity, 10);
        if (isTruckCat && truckBand) veh.truck_tonnage_band = truckBand;
        if (isCraneCat && craneBand) veh.crane_tonnage_band = craneBand;
        if (isLuxuryCat) {
          veh.luxury_with_driver = true;
          if (luxuryStyle) veh.luxury_style = luxuryStyle;
          if (luxuryHourlyRate) veh.luxury_hourly_rate = Number(luxuryHourlyRate);
          if (luxuryDailyRate) veh.luxury_daily_rate = Number(luxuryDailyRate);
        }
        payload.vehicle = veh;
      }
      const u = await register(payload);
      toast.success(t("auth.accountCreated"));
      nav(u.role === "supplier" ? "/supplier" : "/customer");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return <AuthShell title={t("auth.createAccount")} subtitle={t("auth.createSub")}>
    <form onSubmit={submit} className="space-y-3" data-testid="register-form">
      <div className="grid grid-cols-2 gap-0 rounded-full bg-ink-surface p-1 border border-ink-border" data-testid="role-toggle">
        <button type="button" onClick={() => setRole("customer")} className={`py-2 rounded-full text-xs font-black transition-colors ${role === "customer" ? "bg-white text-black" : "text-ink-textDim"}`} data-testid="role-customer">
          {t("auth.iAmCustomer")}
        </button>
        <button type="button" onClick={() => setRole("supplier")} className={`py-2 rounded-full text-xs font-black transition-colors ${role === "supplier" ? "bg-brand-accent text-white" : "text-ink-textDim"}`} data-testid="role-supplier">
          {t("auth.iAmSupplier")}
        </button>
      </div>
      <Field label={t("auth.name")} value={name} onChange={setName} testid="register-name" />
      <Field label={t("auth.email")} value={email} onChange={setEmail} type="email" testid="register-email" />
      <Field label={t("auth.password")} value={password} onChange={setPassword} type="password" testid="register-password" />
      <Field
        label={t("auth.inviteCode", "Invite code (optional)")}
        value={inviteCode}
        onChange={(v) => setInviteCode(v.toUpperCase())}
        placeholder="DR-XXXXXX"
        testid="register-invite-code"
        required={false}
      />

      {role === "supplier" && (
        <div className="space-y-3 pt-3 border-t border-ink-border" data-testid="supplier-fields">
          <div className="text-[10px] uppercase font-black text-brand-accent tracking-widest">
            {t("auth.driverProfile", "Driver profile")}
          </div>
          <Field label={t("auth.phone", "Phone number")} value={phone} onChange={setPhone} type="tel" testid="register-phone" />
          <label className="block" data-testid="photo-field">
            <div className="text-[10px] uppercase font-black text-ink-textDim mb-1">
              {t("auth.photo", "Profile photo")} <span className="opacity-60">({t("auth.shownToCustomers", "shown to customers")})</span>
            </div>
            <div className="flex items-center gap-3">
              {picture ? (
                <img src={picture} alt="preview" className="w-14 h-14 rounded-2xl object-cover border-2 border-brand-accent" data-testid="photo-preview" />
              ) : (
                <div className="w-14 h-14 rounded-2xl bg-ink-surface2 border border-dashed border-ink-border flex items-center justify-center text-ink-textDim text-[9px] uppercase font-black" data-testid="photo-placeholder">
                  {t("auth.noPhoto", "no photo")}
                </div>
              )}
              <label className="flex-1 cursor-pointer bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2 text-xs font-black inline-flex items-center justify-center hover:border-white">
                {picture ? t("auth.changePhoto", "Change photo") : t("auth.uploadPhoto", "Upload photo")}
                <input type="file" accept="image/*" onChange={onPickPhoto} className="hidden" data-testid="photo-input" />
              </label>
            </div>
          </label>
          <div className="text-[10px] uppercase font-black text-ink-textDim tracking-widest pt-1">
            {t("auth.vehicle", "Your vehicle")}
          </div>
          <Field label={t("auth.plate", "License plate")} value={plate} onChange={setPlate} testid="register-plate" />
          <div className="grid grid-cols-2 gap-2">
            <Field label={t("auth.color", "Color")} value={color} onChange={setColor} testid="register-color" />
            <Field label={t("auth.model", "Make & model")} value={model} onChange={setModel} testid="register-model" />
          </div>

          <label className="block">
            <div className="text-[10px] uppercase font-black text-ink-textDim mb-1">
              {t("auth.primaryService", "Primary service")}
            </div>
            <select
              value={supplierCategory}
              onChange={(e) => setSupplierCategory(e.target.value)}
              className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-3 text-sm outline-none focus:border-white"
              data-testid="register-primary-category"
            >
              <option value="">{t("auth.selectService", "Select…")}</option>
              {["taxi","bus","luxury","crane","towing","mechanic","tire","moving","transport","delivery","carwash"].map((c) => (
                <option key={c} value={c}>{t(`categories.${c}`, c)}</option>
              ))}
            </select>
          </label>

          {isTaxiCat && (
            <div className="space-y-2 rounded-xl border border-brand-accent/40 bg-brand-accent/5 p-3" data-testid="supplier-taxi-specs">
              <div className="text-[10px] uppercase font-black text-brand-accent tracking-widest">
                {t("auth.taxiClass", "Taxi class")}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(specs?.taxi?.types || []).map((tt) => (
                  <button
                    key={tt.id}
                    type="button"
                    onClick={() => { setTaxiType(tt.id); if (!seatingCapacity) setSeatingCapacity(String(tt.seats)); }}
                    className={`rounded-xl p-2 border-2 text-start ${taxiType === tt.id ? "border-white bg-white/10" : "border-ink-border"}`}
                    data-testid={`register-taxi-${tt.id}`}
                  >
                    <div className="text-[11px] font-black text-white">{t(`taxi.${tt.id}`, tt.id)}</div>
                    <div className="text-[10px] text-ink-textDim">
                      {tt.seats} {t("taxi.seatsShort", "seats")} · ×{tt.multiplier.toFixed(1)}
                    </div>
                  </button>
                ))}
              </div>
              <Field label={t("auth.seatingCapacity", "Seating capacity")} value={seatingCapacity} onChange={setSeatingCapacity} type="number" testid="register-seats" />
            </div>
          )}

          {isTruckCat && (
            <div className="space-y-2 rounded-xl border border-brand-accent/40 bg-brand-accent/5 p-3" data-testid="supplier-truck-specs">
              <div className="text-[10px] uppercase font-black text-brand-accent tracking-widest">
                {t("auth.truckTonnage", "Truck capacity")}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(specs?.truck?.bands || []).map((band) => (
                  <button
                    key={band.id}
                    type="button"
                    onClick={() => setTruckBand(band.id)}
                    className={`rounded-xl p-2 border-2 text-start ${truckBand === band.id ? "border-white bg-white/10" : "border-ink-border"}`}
                    data-testid={`register-truck-${band.id}`}
                  >
                    <div className="text-[11px] font-black text-white">{t(`truck.${band.id}`, band.id)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {isBusCat && (
            <div className="space-y-2 rounded-xl border border-brand-accent/40 bg-brand-accent/5 p-3" data-testid="supplier-bus-specs">
              <div className="text-[10px] uppercase font-black text-brand-accent tracking-widest">
                {t("auth.busTier", "Bus tier")}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {(specs?.bus?.types || []).map((bt) => (
                  <button
                    key={bt.id}
                    type="button"
                    onClick={() => { setBusType(bt.id); if (!seatingCapacity) setSeatingCapacity(String(bt.seats)); }}
                    className={`rounded-xl p-2 border-2 text-start ${busType === bt.id ? "border-white bg-white/10" : "border-ink-border"}`}
                    data-testid={`register-bus-${bt.id}`}
                  >
                    <div className="text-[11px] font-black text-white">{t(`bus.${bt.id}`, bt.id)}</div>
                    <div className="text-[10px] text-ink-textDim">
                      {bt.seats} {t("taxi.seatsShort", "seats")}
                    </div>
                  </button>
                ))}
              </div>
              <Field label={t("auth.seatingCapacity", "Seating capacity")} value={seatingCapacity} onChange={setSeatingCapacity} type="number" testid="register-seats-bus" />
            </div>
          )}

          {isCraneCat && (
            <div className="space-y-2 rounded-xl border border-brand-accent/40 bg-brand-accent/5 p-3" data-testid="supplier-crane-specs">
              <div className="text-[10px] uppercase font-black text-brand-accent tracking-widest">
                {t("auth.craneTonnage", "Crane tonnage")}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(specs?.crane?.bands || []).map((band) => (
                  <button
                    key={band.id}
                    type="button"
                    onClick={() => setCraneBand(band.id)}
                    className={`rounded-xl p-2 border-2 text-start ${craneBand === band.id ? "border-white bg-white/10" : "border-ink-border"}`}
                    data-testid={`register-crane-${band.id}`}
                  >
                    <div className="text-[11px] font-black text-white">{t(`crane.${band.id}`, band.id)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {isLuxuryCat && (
            <div className="space-y-2 rounded-xl border border-amber-400/40 bg-amber-400/5 p-3" data-testid="supplier-luxury-specs">
              <div className="text-[10px] uppercase font-black text-amber-300 tracking-widest">
                {t("auth.luxurySpecs", "Luxury vehicle")}
              </div>
              <div className="flex flex-wrap gap-2">
                {["sedan","suv","limousine","supercar","convertible"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setLuxuryStyle(s)}
                    className={`rounded-full px-3 py-1.5 text-[11px] font-black border-2 ${luxuryStyle === s ? "border-amber-400 bg-amber-400/10 text-amber-200" : "border-ink-border text-ink-textDim"}`}
                    data-testid={`register-luxury-style-${s}`}
                  >
                    {t(`luxury.style_${s}`, s.charAt(0).toUpperCase() + s.slice(1))}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label={t("auth.hourlyRate", "Hourly rate")} value={luxuryHourlyRate} onChange={setLuxuryHourlyRate} type="number" testid="register-luxury-hourly" />
                <Field label={t("auth.dailyRate", "Daily rate")} value={luxuryDailyRate} onChange={setLuxuryDailyRate} type="number" testid="register-luxury-daily" />
              </div>
              <div className="text-[10px] text-ink-textDim">
                {t("auth.luxuryHint", "All luxury rentals are chauffeur-driven. Leave a rate blank if you don't offer that billing mode.")}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="text-[10.5px] text-ink-textDim leading-relaxed">{t("auth.freeSignupNote")}</div>
      <button type="submit" disabled={busy} className="pill-btn w-full bg-white text-black rounded-full py-3.5 font-black flex items-center justify-center gap-2 disabled:opacity-50" data-testid="register-submit">
        {busy && <Loader2 className="animate-spin" size={16} />} {t("auth.createAccount")}
      </button>
    </form>
    <GoogleAuthButton />
    <div className="text-center text-xs text-ink-textDim mt-6">
      {t("auth.haveAccount")} <Link to="/login" className="text-white font-black" data-testid="link-login">{t("auth.signIn")}</Link>
    </div>
  </AuthShell>;
}

function AuthShell({ title, subtitle, children }) {
  const nav = useNavigate();
  const { i18n } = useTranslation();
  return (
    <div className="min-h-screen bg-ink-bg text-white">
      <div className="px-5 py-5 flex items-center justify-between">
        <button onClick={() => nav("/")} className="w-9 h-9 rounded-full bg-ink-surface border border-ink-border flex items-center justify-center rtl-flip" data-testid="back-btn"><ChevronLeft size={18} /></button>
        {/* Language switcher — prominent on the auth page so international
            users pick their language BEFORE typing anything. Huge conversion
            lift for AR/HE/RU/TR/FR sign-ups. */}
        <div className="flex items-center gap-1 rounded-full bg-ink-surface border border-ink-border p-1" data-testid="auth-language-switcher">
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => applyLang(l.code)}
              className={`px-2.5 py-1 text-[11px] font-black rounded-full transition ${
                i18n.resolvedLanguage === l.code
                  ? "bg-white text-obsidian"
                  : "text-white/60 hover:text-white"
              }`}
              data-testid={`auth-lang-${l.code}`}
              aria-label={l.name}
              title={l.name}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
      <div className="max-w-md mx-auto px-4">
        <div className="bg-ink-surface rounded-3xl border border-ink-border p-6">
          <div className="text-[11px] uppercase font-black tracking-widest text-ink-textDim">{subtitle}</div>
          <h1 className="text-3xl font-black mt-1">{title}</h1>
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder, required = true }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black text-ink-textDim mb-1">{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        placeholder={placeholder}
        className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-3 text-sm outline-none focus:border-white placeholder:text-white/25"
        data-testid={testid} />
    </label>
  );
}

function GoogleAuthButton() {
  const { t } = useTranslation();
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="mt-4">
      <div className="flex items-center gap-3 my-3">
        <div className="flex-1 h-px bg-ink-border" />
        <div className="text-[10px] uppercase font-black text-ink-textFaint">{t("auth.or")}</div>
        <div className="flex-1 h-px bg-ink-border" />
      </div>
      <button onClick={onClick} className="pill-btn w-full bg-ink-surface2 border border-ink-border rounded-full py-3.5 text-sm font-black flex items-center justify-center gap-3" data-testid="google-auth-btn">
        <svg viewBox="0 0 24 24" width="18" height="18"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
        {t("auth.continueWithGoogle")}
      </button>
    </div>
  );
}
